import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlacementAuth } from '../context/PlacementAuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Target, 
  Shield, 
  Award, 
  Lock, 
  CheckCircle, 
  ArrowRight,
  Loader2,
  Eye,
  EyeOff,
  Zap,
  TrendingUp,
  Users,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/placement`;

export default function PlacementAILanding() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = usePlacementAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    years_of_experience: ''
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/placement-ai/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      toast.success(response.data.message);
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        login(response.data.token, response.data.student);
        toast.success('Login successful!');
        navigate('/placement-ai/dashboard');
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        login(response.data.token, response.data.student);
        toast.success('Registration successful! Please wait for admin approval.');
        navigate('/placement-ai/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Gated Execution",
      description: "Step-by-step workflow that ensures you complete each phase before moving forward"
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Real-time Scoring",
      description: "Dynamic scoreboard tracking compliance, discipline, and outcomes"
    },
    {
      icon: <Lock className="h-6 w-6" />,
      title: "Zero Shortcuts",
      description: "Auto-locks on sequence breaks. Discipline determines outcomes."
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Certification",
      description: "Earn your internship certificate with verified metrics"
    }
  ];

  const workflowSteps = [
    { step: 1, title: "OG Success Plan", desc: "Upload your success plan document" },
    { step: 2, title: "Portfolio Submission", desc: "Submit all required portfolio content" },
    { step: 3, title: "Admin Review", desc: "Get your submissions reviewed" },
    { step: 4, title: "1:1 Session", desc: "Complete your one-on-one session" },
    { step: 5, title: "Corrections", desc: "Confirm all corrections completed" },
    { step: 6, title: "Decision Node", desc: "Choose Internship or Direct Outreach" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-2 rounded-lg">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Placement AI</h1>
              <p className="text-xs text-slate-400">by HireForSkillz</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowAuth(true)}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 px-4 py-2 rounded-full mb-6">
            <Shield className="h-4 w-4 text-amber-500" />
            <span className="text-amber-500 text-sm font-medium">Controlled Execution Engine</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Your Path to
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent"> Placement</span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            A gated execution workflow that enforces discipline, eliminates errors, 
            and tracks your compliance in real-time. This is not a tracker — 
            this is a <span className="text-white font-semibold">controlled execution engine</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => { setShowAuth(true); setIsLogin(false); }}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white h-14 px-8 text-lg"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => { setShowAuth(true); setIsLogin(true); }}
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 h-14 px-8 text-lg"
            >
              Already Registered? Login
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Built for <span className="text-amber-500">Execution Excellence</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Steps */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            The <span className="text-amber-500">Gated Workflow</span>
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Each step unlocks only after completing the previous one. No shortcuts.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflowSteps.map((step, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4"
              >
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h4 className="text-white font-semibold">{step.title}</h4>
                  <p className="text-slate-400 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-amber-500 mb-2">100</div>
              <div className="text-slate-400 text-sm">Total Score Points</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-amber-500 mb-2">14</div>
              <div className="text-slate-400 text-sm">Day Outreach Program</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-amber-500 mb-2">5</div>
              <div className="text-slate-400 text-sm">Gated Steps</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-amber-500 mb-2">75+</div>
              <div className="text-slate-400 text-sm">Score for Referral</div>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-900 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-center">
                {isLogin ? 'Welcome Back' : 'Start Your Journey'}
              </CardTitle>
              <p className="text-slate-400 text-center text-sm">
                {isLogin ? 'Login to continue your placement journey' : 'Register for Placement AI'}
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    <div>
                      <Label className="text-slate-300">Full Name</Label>
                      <Input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Years of Experience</Label>
                      <Input
                        type="text"
                        required
                        value={formData.years_of_experience}
                        onChange={(e) => setFormData({...formData, years_of_experience: e.target.value})}
                        className="bg-white/10 border-white/20 text-white"
                        placeholder="e.g., 0-1 years, 2-3 years"
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="you@example.com"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="bg-white/10 border-white/20 text-white pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Please wait...</>
                  ) : (
                    isLogin ? 'Login' : 'Register'
                  )}
                </Button>

                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-center text-sm text-slate-400 hover:text-amber-400 transition-colors"
                  >
                    Forgot Password?
                  </button>
                )}

                <p className="text-center text-sm text-slate-400">
                  {isLogin ? "Don't have an account? " : "Already registered? "}
                  <button
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className="text-amber-500 hover:text-amber-400 font-medium"
                  >
                    {isLogin ? 'Register' : 'Login'}
                  </button>
                </p>
              </form>

              <button
                onClick={() => setShowAuth(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-slate-900 border-white/10 relative">
            <CardHeader>
              <CardTitle className="text-white text-center">Forgot Password</CardTitle>
              <p className="text-slate-400 text-center text-sm">
                Enter your email and we'll notify the admin to reset your password
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white"
                    placeholder="you@example.com"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  disabled={forgotPasswordLoading}
                >
                  {forgotPasswordLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                  ) : (
                    'Submit Request'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(false); setShowAuth(true); }}
                  className="w-full text-center text-sm text-slate-400 hover:text-white"
                >
                  Back to Login
                </button>
              </form>

              <button
                onClick={() => setShowForgotPassword(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © 2026 HireForSkillz. Placement AI - Discipline Determines Outcomes.
          </p>
        </div>
      </footer>
    </div>
  );
}
